// The ten product-scope questions. Nontechnical language; the system derives
// architecture later. type: 'text' | 'choice' | 'choice_detail'
export const QUESTIONS = [
  { id: "q1", type: "text", eyebrow: "01 / WHAT YOU'RE BUILDING",
    title: "What are you building?",
    help: "Describe the product in your own words. What do you imagine someone being able to do with it?",
    examples: "An app · a website · a platform · a business tool · a marketplace · a customer portal · something new" },
  { id: "q2", type: "text", eyebrow: "02 / WHO IT'S FOR",
    title: "Who is it for?",
    help: "Who will use this product? You can describe one or several audiences.",
    examples: "Customers · employees · businesses · creators · students · patients · members · vendors · the public" },
  { id: "q3", type: "text", eyebrow: "03 / WHAT THEY NEED TO DO",
    title: "What does the user need to do?",
    help: "What are the most important things someone needs to be able to do?",
    examples: "Book · buy · sell · upload · communicate · track · create · search · apply · manage · generate" },
  { id: "q4", type: "text", eyebrow: "04 / START TO FINISH",
    title: "What happens from start to finish?",
    help: "Imagine someone using it for the first time. What happens first, next, and when they're finished?",
    examples: "Walk us through it like a story — no technical language needed." },
  { id: "q5", type: "choice_detail", eyebrow: "05 / ACCOUNTS & USERS",
    title: "Does it need accounts or different types of users?",
    help: "Does everyone use the product the same way, or are there different people with different access?",
    options: ["Everyone uses it the same way", "Users have accounts", "There are different types of users", "There will be administrators", "I'm not sure"],
    detailWhen: ["There are different types of users", "There will be administrators"],
    detailLabel: "Tell us about the different people who'll use it." },
  { id: "q6", type: "choice_detail", eyebrow: "06 / MONEY",
    title: "Does money move through the product?",
    help: "Will the product need to make money or handle payments?",
    options: ["No", "Users will pay for something", "There will be subscriptions or memberships", "Users will buy from or sell to each other", "Payments will happen in another system", "I'm not sure"],
    detailWhen: ["Users will pay for something", "There will be subscriptions or memberships", "Users will buy from or sell to each other", "Payments will happen in another system"],
    detailLabel: "Optional — tell us a bit more." },
  { id: "q7", type: "choice_detail", eyebrow: "07 / CONNECTIONS",
    title: "Does it need to connect to other systems?",
    help: "Does your product need to connect to anything else?",
    options: ["Yes", "No", "I'm not sure"],
    detailWhen: ["Yes"],
    detailLabel: "Tell us what you want it to connect to, even if you don't know the technical name.",
    examples: "Stripe · Google · calendars · email · SMS · CRM · social platforms · accounting · other software" },
  { id: "q8", type: "choice_detail", eyebrow: "08 / AI & AUTOMATION",
    title: "Does it need AI or automation?",
    help: "What would you like the product to do automatically or intelligently? You don't have to decide whether AI belongs — we'll determine that.",
    options: ["Yes, something automatic or intelligent", "No", "I'm not sure whether AI is needed"],
    detailWhen: ["Yes, something automatic or intelligent"],
    detailLabel: "What would you like it to do automatically or intelligently?",
    examples: "Answer questions · analyze · generate content · recommend · route requests · follow up · process documents" },
  { id: "q9", type: "text", eyebrow: "09 / FIRST VERSION",
    title: "What would make the first version successful?",
    help: "If we built the first version, what absolutely has to work for you to say \"yes, this works\"? And what could wait until later?",
    examples: "Separate the must-haves from the future vision." },
  { id: "q10", type: "choice_detail", eyebrow: "10 / THE FUTURE",
    title: "Where do you see this going?",
    help: "What do you ultimately want this product to become?",
    options: ["A simple tool", "A business", "A revenue-producing product", "A larger platform", "A company", "An internal system", "A product used by thousands or millions", "I don't know yet"],
    detailWhen: ["A simple tool", "A business", "A revenue-producing product", "A larger platform", "A company", "An internal system", "A product used by thousands or millions", "I don't know yet"],
    detailLabel: "Tell us what you imagine this becoming if it works." },
];

// Smart follow-up: return AT MOST ONE tailored clarification, chosen when an
// answer clearly calls for it. Deterministic (no external AI during the
// questionnaire — that respects the Phase 2 confidentiality boundary).
export function deriveClarifications(responses) {
  const val = (k) => (responses[k]?.value || "").toLowerCase();
  const det = (k) => (responses[k]?.detail || "").toLowerCase();
  const textBlob = ["q1", "q3", "q4", "q9"].map(val).join(" ");
  const q6 = val("q6"), q7 = val("q7") + " " + det("q7"), q8 = val("q8");

  const rules = [
    [/(marketplace|buyers?|sellers?|vendors?|listing)/.test(textBlob + " " + q6),
      { id: "c_marketplace", title: "What happens after a buyer purchases something?",
        help: "Walk us through what should happen once a purchase is made — fulfilment, payout, confirmation, and so on." }],
    [/(appointment|booking|schedule|reservation|calendar)/.test(textBlob + " " + q7),
      { id: "c_booking", title: "Who manages availability and appointments?",
        help: "Tell us who sets availability and who confirms or manages bookings." }],
    [/subscription|membership/.test(q6),
      { id: "c_subscription", title: "What exactly will people pay for, and how often?",
        help: "Describe the plans or tiers and what each unlocks." }],
    [q8.includes("yes"),
      { id: "c_ai_oversight", title: "What decision should a person always be able to review or override?",
        help: "Where should a human stay in control of what the automation or AI does?" }],
    [/(upload|document|file|photo|video|attachment)/.test(textBlob),
      { id: "c_uploads", title: "What kinds of files will people upload, and who can see them?",
        help: "Tell us the file types and who should be able to view or manage them." }],
  ];

  for (const [match, q] of rules) {
    if (match) return [{ ...q, eyebrow: "ONE QUICK CLARIFICATION" }];
  }
  return [];
}
